/**
 * useCarpoolRequests — polls GET /api/carpool/pending every 3s and exposes
 * accept + simulate helpers.
 *
 * Phase A of the carpool rider ↔ admin flow. WebSocket sync arrives in
 * Phase D; polling is honest, simple, and sufficient for demo cadence.
 */
import { useCallback, useEffect, useRef, useState } from "react";
const API_BASE = (typeof window !== "undefined" &&
    window.__API_BASE) ||
    "https://bunching-brain-backend.onrender.com";
const WS_BASE = API_BASE.replace(/^http/, "ws");
// Polling stays armed as a fallback in case WS fails (Render Free cold
// start, mobile network hiccup, CF Pages proxy quirks). WS pushes make
// updates near-instant; the poll just guarantees the queue is never
// wrong for more than POLL_MS.
const POLL_MS = 5000;
// Mumbai MMR anchors used by the "simulate rider" button.
const PICKUP_POOL = [
    { lat: 19.033, lng: 73.02, label: "Kamothe Sec-6" },
    { lat: 19.077, lng: 72.997, label: "Vashi Station" },
    { lat: 19.216, lng: 72.977, label: "Ghatkopar E" },
    { lat: 19.116, lng: 72.914, label: "Chakala JB Nagar" },
    { lat: 19.196, lng: 73.089, label: "Dombivli E" },
];
const DEST_POOL = [
    { lat: 19.076, lng: 72.877, label: "Andheri E" },
    { lat: 19.05, lng: 73.037, label: "Panvel Depot" },
    { lat: 19.114, lng: 72.836, label: "Bandra E" },
    { lat: 19.24, lng: 73.13, label: "Kalyan Station" },
];
let riderCounter = 0;
function nextRiderId() {
    riderCounter += 1;
    return `sim-rider-${Date.now().toString(36)}-${riderCounter}`;
}
export function useCarpoolRequests(enabled = true) {
    const [pending, setPending] = useState([]);
    // Phase D — active rides are WS-driven (driver_position + request_updated
    // events). Not polled: any admin viewing before an accept sees them appear
    // as soon as the simulator's first tick lands (~800 ms after accept).
    const [active, setActive] = useState(() => new Map());
    // Phase E — recent pool merges (dedup by joined_request_id). Grows on
    // pool_merge WS events; also seeded from /api/carpool/merges at mount +
    // reconnect so a mid-session refresh doesn't lose history.
    const [merges, setMerges] = useState([]);
    const [latestMergeAt, setLatestMergeAt] = useState(null);
    const [impact, setImpact] = useState({
        pools_today: 0,
        co2_kg: 0,
        inr_saved: 0,
        riders_served: 0,
    });
    const [loading, setLoading] = useState(true);
    const [lastError, setLastError] = useState(null);
    const cancelledRef = useRef(false);
    const mergesSeenRef = useRef(new Set());
    const refetch = useCallback(async () => {
        try {
            const r = await fetch(`${API_BASE}/api/carpool/pending`);
            if (!r.ok)
                throw new Error(`pending: HTTP ${r.status}`);
            const j = (await r.json());
            if (cancelledRef.current)
                return;
            setPending(j.requests ?? []);
            setLastError(null);
        }
        catch (e) {
            if (cancelledRef.current)
                return;
            setLastError(e instanceof Error ? e.message : String(e));
        }
        finally {
            if (!cancelledRef.current)
                setLoading(false);
        }
    }, []);
    useEffect(() => {
        if (!enabled)
            return undefined;
        cancelledRef.current = false;
        void refetch();
        // Phase E — seed impact + recent merges so the header ticker and
        // RailCarousel slide have historical context on mount / reconnect.
        void fetch(`${API_BASE}/api/carpool/impact`)
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => {
            if (j && !cancelledRef.current) {
                setImpact({
                    pools_today: Number(j.pools_today ?? 0),
                    co2_kg: Number(j.co2_kg ?? 0),
                    inr_saved: Number(j.inr_saved ?? 0),
                    riders_served: Number(j.riders_served ?? 0),
                });
            }
        })
            .catch(() => { });
        void fetch(`${API_BASE}/api/carpool/merges`)
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => {
            if (j &&
                Array.isArray(j.merges) &&
                !cancelledRef.current) {
                const seeded = j.merges.slice(0, 20).map((m) => ({
                    at: new Date(m.at).getTime(),
                    primary_request_id: m.primary_request_id,
                    joined_request_id: m.joined_request_id,
                    detour_km: m.detour_km,
                    co2_saved_kg: m.co2_saved_kg,
                    inr_saved: m.inr_saved,
                    driver_id: m.driver_id,
                    driver_name: m.driver_name,
                }));
                for (const s of seeded)
                    mergesSeenRef.current.add(s.joined_request_id);
                setMerges(seeded);
            }
        })
            .catch(() => { });
        const t = window.setInterval(refetch, POLL_MS);
        // Phase C — WS subscription. On any event (new_request /
        // request_updated), re-fetch the queue so state stays in sync
        // without waiting for the next poll tick.
        let ws = null;
        let retryTimer;
        let closed = false;
        const connect = () => {
            if (closed)
                return;
            try {
                ws = new WebSocket(`${WS_BASE}/ws/carpool?role=admin`);
            }
            catch {
                // Some browsers throw on invalid URLs. Fall back to polling only.
                return;
            }
            ws.onmessage = (ev) => {
                // Phase D — inspect the payload so we can upsert active rides
                // in-place (fast) and only re-fetch pending on state changes
                // that actually affect the queue.
                let msg = null;
                try {
                    msg = JSON.parse(ev.data);
                }
                catch {
                    msg = null;
                }
                const rec = msg?.request;
                const kind = msg?.type;
                if (rec) {
                    if (kind === "driver_position" ||
                        kind === "pool_merge" ||
                        (kind === "request_updated" &&
                            (rec.state === "assigned" || rec.state === "en_route"))) {
                        setActive((prev) => {
                            const next = new Map(prev);
                            next.set(rec.request_id, rec);
                            return next;
                        });
                    }
                    if (kind === "request_updated" &&
                        (rec.state === "completed" ||
                            rec.state === "cancelled" ||
                            rec.state === "arrived")) {
                        setActive((prev) => {
                            if (!prev.has(rec.request_id))
                                return prev;
                            const next = new Map(prev);
                            next.delete(rec.request_id);
                            return next;
                        });
                    }
                }
                // Phase E — Convergence: a pool_merge event fires twice (once per
                // rider room + admin fanout); dedupe by joined_request_id.
                if (kind === "pool_merge" && msg?.pool_info) {
                    const info = msg.pool_info;
                    if (!mergesSeenRef.current.has(info.joined_request_id)) {
                        mergesSeenRef.current.add(info.joined_request_id);
                        const now = Date.now();
                        const driver = rec?.driver ?? null;
                        const evt = {
                            at: now,
                            primary_request_id: info.primary_request_id,
                            joined_request_id: info.joined_request_id,
                            detour_km: info.detour_km,
                            co2_saved_kg: info.co2_saved_kg,
                            inr_saved: info.inr_saved,
                            driver_name: driver?.name,
                            driver_vehicle: driver?.vehicle,
                            driver_id: driver?.id,
                        };
                        setMerges((prev) => [evt, ...prev].slice(0, 20));
                        setLatestMergeAt(now);
                    }
                    // Refresh the impact ticker after each merge (small race is fine).
                    void fetch(`${API_BASE}/api/carpool/impact`)
                        .then((r) => (r.ok ? r.json() : null))
                        .then((j) => {
                        if (j && !cancelledRef.current) {
                            setImpact({
                                pools_today: Number(j.pools_today ?? 0),
                                co2_kg: Number(j.co2_kg ?? 0),
                                inr_saved: Number(j.inr_saved ?? 0),
                                riders_served: Number(j.riders_served ?? 0),
                            });
                        }
                    })
                        .catch(() => { });
                }
                // Any event potentially affects the pending queue (new_request adds
                // one; request_updated may have removed one). Refetch to stay honest.
                void refetch();
            };
            ws.onerror = () => {
                try {
                    ws?.close();
                }
                catch { /* noop */ }
            };
            ws.onclose = () => {
                if (closed)
                    return;
                // Reconnect with a small backoff so a flaky link doesn't spin the CPU.
                retryTimer = window.setTimeout(connect, 3000);
            };
        };
        connect();
        return () => {
            cancelledRef.current = true;
            closed = true;
            window.clearInterval(t);
            if (retryTimer)
                window.clearTimeout(retryTimer);
            if (ws)
                try {
                    ws.close();
                }
                catch { /* noop */ }
        };
    }, [enabled, refetch]);
    const accept = useCallback(async (request_id, driver_id) => {
        try {
            const r = await fetch(`${API_BASE}/api/carpool/request/${encodeURIComponent(request_id)}/accept`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ driver_id: driver_id ?? null }),
            });
            if (!r.ok)
                throw new Error(`accept: HTTP ${r.status}`);
            const j = (await r.json());
            await refetch();
            return j;
        }
        catch (e) {
            setLastError(e instanceof Error ? e.message : String(e));
            return null;
        }
    }, [refetch]);
    const simulate = useCallback(async () => {
        const pickup = PICKUP_POOL[Math.floor(Math.random() * PICKUP_POOL.length)];
        const dest = DEST_POOL[Math.floor(Math.random() * DEST_POOL.length)];
        try {
            const r = await fetch(`${API_BASE}/api/carpool/request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    rider_id: nextRiderId(),
                    pickup_lat: pickup.lat,
                    pickup_lng: pickup.lng,
                    dest_lat: dest.lat,
                    dest_lng: dest.lng,
                    pickup_label: pickup.label,
                    dest_label: dest.label,
                }),
            });
            if (!r.ok)
                throw new Error(`simulate: HTTP ${r.status}`);
            const j = (await r.json());
            await refetch();
            return j;
        }
        catch (e) {
            setLastError(e instanceof Error ? e.message : String(e));
            return null;
        }
    }, [refetch]);
    // Convert active Map → array for consumers. Sorted by most-recent update
    // so downstream code (e.g. UI overlays) can slice(0, N) if needed.
    const activeList = Array.from(active.values()).sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    return {
        pending,
        active: activeList,
        merges,
        latestMergeAt,
        impact,
        loading,
        lastError,
        refetch,
        accept,
        simulate,
    };
}
